import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AdjacentType, BranchingQueryDefinition, NodeEdgeMapper } from '@iotv/datamodel-core';
import { useEffect, useState } from 'react';
import { getGraphQueryRequestV2 } from '../../../data/daoFunctions/GraphQueries/recursiveFilteredGraphQuery';
import { iterateQueryWithSource } from '../../../data/Wideawake/WideawakeWrapper';
import { ExpressionFunctionType, LogicalOperatorType } from '../../../types';
import ExpandableTable from '../Expandable/ExpandableTable';
import { customerDefinition, deviceDefinition, DeviceExport } from './DeviceDefinitions';
import { Grid } from '@mui/material';
const getDeviceEntity = () => {
    const apexQuery = new BranchingQueryDefinition({
        id: 'apex',
        path: [{ adjacencyType: AdjacentType.SUBJECT, objectTypeId: 'Device' }],
        subQueries: [],
    });
    const deviceToCustomer = new BranchingQueryDefinition({
        id: `deviceToCustomer`,
        path: [
            {
                objectTypeId: 'Customer',
                adjacencyType: AdjacentType.PARENT,
                edgeTypeId: undefined,
            },
        ],
        subQueries: [],
    });
    const deviceToGroup = new BranchingQueryDefinition({
        id: `deviceToGroup`,
        path: [
            {
                objectTypeId: 'Group',
                adjacencyType: AdjacentType.PARENT,
                edgeTypeId: undefined,
                nodeFilters: [
                    {
                        filters: [
                            {
                                key: 'supportedType',
                                predicate: ExpressionFunctionType.CONTAINS,
                                value: 'Device',
                            },
                        ],
                        setOperation: LogicalOperatorType.AND,
                    },
                ],
            },
        ],
        subQueries: [],
    });
    const groupToCustomer = new BranchingQueryDefinition({
        id: `groupToCustomer`,
        path: [
            {
                objectTypeId: 'Customer',
                adjacencyType: AdjacentType.PARENT,
                edgeTypeId: undefined,
            },
        ],
        subQueries: [],
    });
    apexQuery.addSubQuery(deviceToCustomer);
    apexQuery.addSubQuery(deviceToGroup);
    deviceToGroup.addSubQuery(groupToCustomer);
    const res = {
        query: apexQuery,
        dehydrated: apexQuery.dehydrate(),
    };
    return res;
};
const DeviceCount = () => {
    const [directLinkMapper, setDirectLinkMapper] = useState(undefined);
    const [groupLinkMapper, setGroupLinkMapper] = useState(undefined);
    useEffect(() => {
        const localDirectMapper = new NodeEdgeMapper();
        const localGroupMapper = new NodeEdgeMapper();
        const createDeviceMapper = async () => {
            // get devices that have been seen
            const params = [
                {
                    key: 'pk',
                    value: 'Device',
                    predicate: 'begins_with',
                },
                {
                    key: 'lastSeen', //appEui
                    predicate: 'attribute_exists',
                },
            ];
            let offlineDevices = (await iterateQueryWithSource('Device', true, params));
            offlineDevices = offlineDevices.filter((val) => val.eui !== undefined && val.eui.length === 16);
            const arrays = [], size = 50;
            while (offlineDevices.length > 0)
                arrays.push(offlineDevices.splice(0, size));
            const devEntPromises = await arrays.map((segment) => {
                return getGraphQueryRequestV2({
                    contextObjects: segment,
                    query: getDeviceEntity().query,
                });
            });
            Promise.all(devEntPromises).then((res) => {
                const allDetails = res
                    .map((val) => {
                    if (val.err === null && val.data !== undefined) {
                        const notDevices = val.data?.getContainedNodesAndEdges() || [];
                        return notDevices;
                    }
                    else {
                        return [];
                    }
                })
                    .flat();
                const groupDetails = res
                    .map((val) => {
                    if (val.err === null && val.data !== undefined) {
                        const localMapper = val.data;
                        const customers = localMapper?.getNodesOfType('Customer') || [];
                        const allLink = customers
                            .map((cust) => {
                            const groupNodes = localMapper?.getChildNodes(cust).filter((val) => val.type === 'Group') || [];
                            const customerDetails = groupNodes
                                .map((group) => {
                                const devNodes = localMapper?.getChildNodes(group).filter((val) => val.type === 'Device') || [];
                                // create edge between customer and dev
                                const edges = devNodes.map((dev) => {
                                    return {
                                        ...dev,
                                        pk: cust.pk,
                                    };
                                });
                                return [...edges, ...devNodes];
                            })
                                .flat();
                            return customerDetails;
                        })
                            .flat();
                        return [...customers, ...allLink];
                    }
                    else {
                        return [];
                    }
                })
                    .flat();
                localDirectMapper.loadItems(allDetails);
                setDirectLinkMapper(localDirectMapper);
                localGroupMapper.loadItems(groupDetails);
                setGroupLinkMapper(localGroupMapper);
            });
        };
        createDeviceMapper();
    }, []);
    return (_jsx("div", { children: _jsxs(Grid, { container: true, children: [_jsx(Grid, { id: "CustomerDevice", container: true, size: { xs: 12, sm: 6 }, sx: { p: 1 }, children: _jsx("div", { style: { width: '100%' }, children: _jsx(ExpandableTable, { header: 'Customer Devices',
                            data: directLinkMapper,
                            parentDefinition: customerDefinition,
                            childDefinition: deviceDefinition,
                            redirect: true,
                            exportHeaders: DeviceExport }) }) }), _jsx(Grid, { id: "CustomerGroupDevice", container: true, size: { xs: 12, sm: 6 }, sx: { p: 1 }, children: _jsx("div", { style: { width: '100%' }, children: _jsx(ExpandableTable, { header: 'Customer Devices via Groups',
                            data: groupLinkMapper,
                            parentDefinition: customerDefinition,
                            childDefinition: deviceDefinition,
                            redirect: true,
                            exportHeaders: DeviceExport }) }) })] }) }));
};
export default DeviceCount;
